"use client";

import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { isAdminEmail } from "@/lib/admin-auth";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";
import { useSession } from "@/session/session-context";

type Viewer = "buyer" | "admin";

type PushMessage = {
  id: string;
  sender: "user" | "admin";
  body: string;
  created_at: string;
};

function fmtShort(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function ThemBubble({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[min(100%,18rem)] whitespace-pre-wrap break-words rounded-[20px] rounded-bl-[6px] bg-[#E9E9EB] px-3.5 py-2.5 text-[15px] leading-snug text-black shadow-sm">
        {children}
      </div>
    </div>
  );
}

function MeBubble({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[min(100%,18rem)] whitespace-pre-wrap break-words rounded-[20px] rounded-br-[6px] bg-[#007AFF] px-3.5 py-2.5 text-[15px] leading-snug text-white shadow-sm">
        {children}
      </div>
    </div>
  );
}

type Props = {
  orderId: string;
  viewer: Viewer;
  lineSummary: string;
  backHref: string;
  backLabel: string;
};

export function PushPrivateThread({ orderId, viewer, lineSummary, backHref, backLabel }: Props) {
  const { user } = useSession();
  const [messages, setMessages] = useState<PushMessage[]>([]);
  const [loadErr, setLoadErr] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const isMine = useCallback(
    (sender: PushMessage["sender"]) => (viewer === "buyer" ? sender === "user" : sender === "admin"),
    [viewer],
  );

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      if (viewer === "admin") {
        if (!isAdminEmail(user?.email)) {
          setLoadErr("Admin sign-in required.");
          return;
        }
        const j = await invokeEdgeFunction<{ messages?: PushMessage[]; error?: string }>("push-messages-list", {
          orderId,
        });
        if (j && typeof j === "object" && "error" in j && j.error) {
          setLoadErr(j.error);
          return;
        }
        setMessages(j.messages ?? []);
        return;
      }

      if (!user?.id) {
        setLoadErr("Sign in required.");
        return;
      }
      const sb = createSupabaseBrowserClient();
      const { data: thread, error: tErr } = await sb
        .from("business_push_threads")
        .select("id")
        .eq("customer_order_id", orderId)
        .maybeSingle();
      if (tErr || !thread) {
        setLoadErr("Thread not ready yet — refresh shortly.");
        return;
      }
      const { data: rows, error: mErr } = await sb
        .from("business_push_messages")
        .select("id, sender, body, created_at")
        .eq("thread_id", thread.id)
        .order("created_at", { ascending: true });
      if (mErr) {
        setLoadErr(mErr.message);
        return;
      }
      setMessages((rows ?? []) as PushMessage[]);
    } catch {
      setLoadErr("Network error.");
    }
  }, [orderId, viewer, user?.email, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = window.setInterval(() => void load(), 12000);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  async function send() {
    const text = draft.trim();
    if (text.length < 1 || sending) return;
    setSendErr("");
    setSending(true);
    try {
      if (viewer === "admin") {
        const j = await invokeEdgeFunction<{ ok?: boolean; error?: string }>("push-admin-message", {
          orderId,
          text,
        });
        if (j && typeof j === "object" && "error" in j && j.error) {
          setSendErr(j.error);
          return;
        }
      } else {
        const sb = createSupabaseBrowserClient();
        const { data: thread, error: tErr } = await sb
          .from("business_push_threads")
          .select("id")
          .eq("customer_order_id", orderId)
          .maybeSingle();
        if (tErr || !thread) {
          setSendErr("Thread not ready.");
          return;
        }
        const { error: insErr } = await sb.from("business_push_messages").insert({
          thread_id: thread.id,
          sender: "user",
          body: text,
        });
        if (insErr) {
          setSendErr(insErr.message);
          return;
        }
      }
      setDraft("");
      await load();
    } catch {
      setSendErr("Network error.");
    } finally {
      setSending(false);
    }
  }

  const title = viewer === "buyer" ? "30-Day Business Push" : "Push thread";

  return (
    <div className="mx-auto flex max-w-md flex-col overflow-hidden rounded-[22px] border border-black/[0.06] bg-[#ECECED] shadow-[0_8px_40px_rgba(0,0,0,0.08)]">
      <header className="flex shrink-0 items-center gap-1 border-b border-black/[0.06] bg-[#F7F7F8]/95 px-2 py-2 backdrop-blur-md">
        <Link to={backHref} className="rounded-lg px-2 py-2 text-[17px] font-normal text-[#007AFF] hover:bg-black/[0.04]">
          ‹ {backLabel}
        </Link>
        <div className="min-w-0 flex-1 text-center">
          <h1 className="truncate text-[17px] font-semibold leading-tight text-zinc-900">{title}</h1>
          <p className="truncate text-[11px] text-zinc-500">Private · {lineSummary}</p>
        </div>
        <div className="w-14 shrink-0" aria-hidden />
      </header>

      <div className="max-h-[min(560px,calc(100dvh-14rem))] flex-1 space-y-3 overflow-y-auto px-3 py-4 [scrollbar-width:thin]">
        {loadErr ? (
          <ThemBubble>
            <p className="text-[14px]">{loadErr}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-2 text-[13px] font-semibold text-[#007AFF]"
            >
              Retry
            </button>
          </ThemBubble>
        ) : messages.length === 0 ? (
          <ThemBubble>
            <p className="text-[15px]">No messages yet. If you just paid, refresh in a moment.</p>
          </ThemBubble>
        ) : (
          messages.map((m) => {
            const mine = isMine(m.sender);
            const Bubble = mine ? MeBubble : ThemBubble;
            return (
              <div key={m.id}>
                <Bubble>
                  <p>{m.body}</p>
                  <p className={`mt-2 text-[10px] ${mine ? "text-white/70" : "text-zinc-500"}`}>{fmtShort(m.created_at)}</p>
                </Bubble>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <footer className="shrink-0 border-t border-black/[0.06] bg-[#F7F7F8]/98 p-3 pb-[max(12px,env(safe-area-inset-bottom))] backdrop-blur-md">
        <div className="flex items-end gap-2 rounded-[28px] border border-black/[0.06] bg-white/90 px-3 py-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            maxLength={8000}
            placeholder={viewer === "buyer" ? "Message…" : "Reply…"}
            className="max-h-32 min-h-[2.5rem] flex-1 resize-none bg-transparent text-[15px] leading-snug text-zinc-900 outline-none placeholder:text-zinc-400"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button
            type="button"
            disabled={sending || draft.trim().length < 1}
            onClick={() => void send()}
            className="shrink-0 rounded-full bg-[#007AFF] px-4 py-2 text-[14px] font-semibold text-white shadow-sm transition hover:bg-[#0066DD] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Send
          </button>
        </div>
        {sendErr ? <p className="mt-2 text-center text-[12px] text-rose-600">{sendErr}</p> : null}
      </footer>
    </div>
  );
}
